import { DocumentTypeName } from 'features/Documents'
import { find } from 'lodash-es'
import { getParent, types } from 'mobx-state-tree'

import { TActivityFeed } from '../ActivityFeed'
import { DOCUMENT_ICON, WARNING_ICON } from '../ActivityFeedIcon'
import { ReferenceFreightieOrUser } from '../Actors'
import { TAvatarType } from '../avatars/AvatarType'
import { getAvatar, getAvatarType, getTitleFromActor } from '../avatars/avatarUtils'
import { DOWNLOAD, IActivityMessageSection, IActivityTitle, ICON, TEXT } from '../messages/MessageSectionType'
import { Activity, TActivity } from './Activity'

const documentActionTranslationMap = {
  uploaded: 'activity-feed_document_uploaded',
  updated: 'activity-feed_document_updated',
  deleted: 'activity-feed_document_deleted',
}

export const DocumentUploadActivity = types
  .compose(
    'DocumentUploadActivity',
    Activity,
    types.model({
      action: types.string, // | created | updated | deleted
      document_id: types.string,
      name: types.string,
      role: types.string,
      actor: ReferenceFreightieOrUser,
    })
  )
  .preProcessSnapshot((snapshot) => {
    const { freightieAuthor_id, userAuthor_id } = snapshot
    return {
      ...snapshot,
      actor: freightieAuthor_id || userAuthor_id,
    }
  })
  .views((self) => ({
    get actionStr(): string {
      return self.action === 'created'
        ? documentActionTranslationMap.uploaded
        : documentActionTranslationMap[self.action]
    },
    get isDeleted(): boolean {
      const activityFeed = getParent(self, 2) as TActivityFeed
      return !!find(activityFeed.deletedDocuments, (activity) => activity.document_id === self.document_id)
    },
  }))
  .views((self) => ({
    get msgTitle(): IActivityMessageSection[] {
      return [
        {
          type: TEXT,
          variant: 'body2-medium',
          contentNode: <DocumentTypeName documentType={self.role} />,
          translate: false,
        },
        {
          type: TEXT,
          content: ' ',
        },
        {
          type: TEXT,
          variant: 'body2-medium',
          content: self.actionStr,
          translate: true,
        },
        {
          type: TEXT,
          content: '\n',
        },
      ]
    },
    get fileLink(): IActivityMessageSection[] {
      if (self.isDeleted) {
        return [
          {
            type: ICON,
            content: WARNING_ICON,
            getStyle: (theme) => ({
              display: 'inline-flex',
              width: 16,
              height: 16,
              transform: 'translateY(2px)',
              marginRight: theme.spacing(0.2),
            }),
          },
          {
            type: TEXT,
            content: 'activity-feed_document_link_deleted',
            style: { fontStyle: 'italic' },
            translate: true,
          },
        ]
      }

      return [
        {
          type: DOWNLOAD,
          _id: self.document_id,
          content: 'activity-feed_document_download',
          name: self.name,
          translate: true,
        },
      ]
    },
    get msgBody(): IActivityMessageSection {
      const whitespace = ' '
      return {
        type: TEXT,
        content: `${self.name}${whitespace}`,
      }
    },
  }))
  .views((self) => ({
    get activityIcon(): string {
      return DOCUMENT_ICON
    },
    get avatarType(): TAvatarType {
      return getAvatarType(self.actor)
    },
    get avatar(): string {
      return getAvatar(self.actor)
    },
    get messageContent(): IActivityMessageSection[] {
      const link = self.action === 'deleted' ? [] : self.fileLink
      return [...self.msgTitle, self.msgBody, ...link]
    },
    get title(): IActivityTitle {
      return getTitleFromActor(self.actor)
    },
  }))

export type TDocumentUploadActivity = typeof DocumentUploadActivity.Type

export function isDocumentActivity(activity: TActivity): activity is TDocumentUploadActivity {
  return activity.type === 'documentUpload'
}
